import React, { useState, useEffect, useCallback } from "react";
import withLanguageContext from "lib/hocs/withLanguageContext";
import SettingStore from "stores/settings";
import * as CONSTANTS from "../constants";
import { getTag, getCategoryID, getTagValue } from "../utils";
import PageFilter from "./PageFilter";
import TabFilter from "./TabFilter";
import MobileFilter from "./MobileFilter";

const mobileViewport = window.matchMedia("(max-width: 600px)").matches || false;

const Filter = ({
  store,
  mode,
  mediaClass,
  totalPercentCompleted,
  languageContext,
}) => {
  const [state, setState] = useState({
    ...store.filter,
    viewport: CONSTANTS.VIEWPORT_MEDIUM,
    showFilter: false,
  });
  const [hitCount, setHitCount] = useState(0);
  const [totaltCount, setTotalCount] = useState(0);

  useEffect(() => {
    const savedSortBy = localStorage.getItem(CONSTANTS.STORAGE_KEY_SORT_ORDER);
    const savedFilterStatus = localStorage.getItem(
      CONSTANTS.STORAGE_KEY_FILTER_STATUS,
    );
    const viewport = mediaClass;
    setState((prevState) => ({
      ...prevState,
      viewport,
      showFilter: viewport === CONSTANTS.VIEWPORT_SMALL,
      sortBy: savedSortBy || CONSTANTS.SORT_TITLE,
      filterStatus: savedFilterStatus || CONSTANTS.PROGRESS_ALL,
    }));

    if (savedSortBy) {
      setSortBy(savedSortBy);
    }

    setTotalCount(store.courses?.length + store.programs?.length ?? 0);
    filterByQueryString();
    window.addEventListener("hashchange", locationHashChanged);
    return () => window.removeEventListener("hashchange", locationHashChanged);
  }, []);

  useEffect(() => {
    if (!store.searching) {
      setHitCount(store.courses?.length + store.programs?.length ?? 0);
    }
  }, [store.filter, store.originalCourses, store.searching]);

  useEffect(() => {
    if (state.sortBy) {
      localStorage.setItem(CONSTANTS.STORAGE_KEY_SORT_ORDER, state.sortBy);
    }
  }, [state.sortBy]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      viewport: mediaClass,
      showFilter: mediaClass === CONSTANTS.VIEWPORT_SMALL,
    }));
  }, [mediaClass]);

  useEffect(() => {
    if (hasStateChanged()) {
      doSearch();
    }
  }, [
    state.searchText,
    state.selectedTags,
    state.categoryId,
    state.hideAdministratorCourses,
    state.hideParticipantCourses,
  ]);

  const hasStateChanged = () => {
    return (
      (state.searchText !== store.filter.searchText &&
        state.searchText === "") ||
      state.selectedTags !== store.filter.selectedTags ||
      state.categoryId !== store.filter.categoryId ||
      state.hideAdministratorCourses !==
        store.filter.hideAdministratorCourses ||
      state.hideParticipantCourses !== store.filter.hideParticipantCourses
    );
  };

  const locationHashChanged = useCallback(() => {
    if (window.location.href.indexOf("?") > -1) {
      filterByQueryString();
      doSearch();
    } else if (state.categoryId !== 0) {
      setState((prevState) => ({
        ...prevState,
        searchText: "",
        categoryId: 0,
      }));
    }
  }, [state.categoryId]);

  const doSearch = useCallback(() => {
    store.search({
      searchText: state.searchText,
      categoryId: state.categoryId,
      selectedTags: state.selectedTags,
      sortBy: state.sortBy,
      hideAdministratorCourses: state.hideAdministratorCourses,
      hideParticipantCourses: state.hideParticipantCourses,
      mode,
    });
  }, [state, store, mode]);

  const filterByQueryString = useCallback(() => {
    const mycoursesfiltertags = SettingStore.getSettingValue(
      "mycoursesfiltertags",
    );
    if (!mycoursesfiltertags) return;

    const tags = mycoursesfiltertags.split(",");
    const { categories, tags: storeTags } = store;

    tags.forEach((tag) => {
      const tagValue = new URLSearchParams(window.location.search).get(tag);
      if (!tagValue) return;

      if (tag === "category") {
        const categoryId = getCategoryID(tagValue, categories);
        setState((prevState) => ({ ...prevState, categoryId }));
      } else {
        const tagData = getTag(tag, storeTags);
        const valueId = getTagValue(tagData, tagValue);
        setTagFilter(0, valueId);
      }
    });
  }, [store]);

  const setFilter = useCallback(
    (f) => {
      let filter;
      if (f !== store.filter.filter) {
        filter = f;
      }
      store.setFilter(filter);
      localStorage.setItem(
        CONSTANTS.STORAGE_KEY_FILTER_STATUS,
        state.filterStatus,
      );
    },
    [store],
  );

  const setSortBy = useCallback(
    (sortBy) => {
      setState((prevState) => ({ ...prevState, sortBy }));
      store.setSortBy(sortBy);
    },
    [store],
  );

  const setSearchText = useCallback((searchText) => {
    setState((prevState) => ({ ...prevState, searchText }));
  }, []);

  const onSearch = useCallback(() => {
    doSearch();
  }, [doSearch]);

  return (
    <div>
      {mode === CONSTANTS.MODE_FULL ? (
        mobileViewport ? (
          <MobileFilter
            store={store}
            mediaClass={mediaClass}
            languageContext={languageContext}
            setFilter={setFilter}
            setSortBy={setSortBy}
            setSearchText={setSearchText}
            state={state}
            setState={setState}
            showHitCount={totaltCount !== hitCount}
            hitCount={hitCount}
            totalPercentCompleted={totalPercentCompleted || 0}
            onSearch={onSearch}
          />
        ) : (
          <PageFilter
            store={store}
            mediaClass={mediaClass}
            languageContext={languageContext}
            setFilter={setFilter}
            setSortBy={setSortBy}
            setSearchText={setSearchText}
            state={state}
            setState={setState}
            showHitCount={totaltCount !== hitCount}
            hitCount={hitCount}
            totalPercentCompleted={totalPercentCompleted || 0}
            onSearch={onSearch}
          />
        )
      ) : (
        !store.searching && (
          <TabFilter
            store={store}
            mediaClass={mediaClass}
            languageContext={languageContext}
            setFilter={setFilter}
            setSortBy={setSortBy}
            setSearchText={setSearchText}
            state={state}
            setState={setState}
            hitCount={hitCount}
            totalPercentCompleted={totalPercentCompleted || 0}
          />
        )
      )}
    </div>
  );
};

export default withLanguageContext(Filter, "ActivityCentre");
